package com.neurobs

import com.neurobs.errors.tryElse
import io.circe.{Encoder, Json}

import scala.collection.Iterable
import scala.collection.mutable.ListBuffer
import scala.collection.mutable.ArrayBuffer
import scala.util.control.NonFatal


package object util {

	implicit val PrettyDoubleEncoder: Encoder[Double] = JsonPrinter.PrettyDoubleEncoder

	/** Calculate median of sequence. Note, this has to sort the sequence. */
	def median(values: Seq[Double]): Double = {
		val (lower, upper) = values.sorted.splitAt(values.size / 2)
		if (values.size % 2 == 0) (lower.last + upper.head) / 2.0 else upper.head
	}

	// http://stackoverflow.com/questions/4753629/how-do-i-make-a-class-generic-for-all-numeric-types
	// http://stackoverflow.com/questions/3498784/scala-calculate-average-of-someobj-double-in-a-listsomeobj/34196631#34196631
	// http://stackoverflow.com/questions/4662292/scala-median-implementation
	/** Implicitly add mean, median and quantile to Seq[Double] */
	implicit class ImplDoubleSeqUtils(values: Seq[Double]) {
		def mean: Double = values.sum / values.length

		//def median: Double =
		lazy val median: Double =	{
			val (lower, upper) = values.sorted.splitAt(values.size / 2)
			if (values.size % 2 == 0) (lower.last + upper.head) / 2.0 else upper.head
		}

		def quantile(quantile:Double): Double = {
			assert(quantile >=0 && quantile <=1)
			// convert quantile into and index
			val quantIndex: Int = (values.length.toDouble*quantile).round.toInt -1
			values.sorted.toList(quantIndex)
		}
	}

	/** Add efficient sorted method to Iterable[Double] */
	implicit class ImplDoubleIterUtils(values: Iterable[Double]) {
		def sorted: Array[Double] = {
			val arr = values.toArray
			java.util.Arrays.sort(arr) // This is what scala's Seq.sort does, except we're skipping superfluous steps.
			arr
		}
	}

	/** Stores progressive data necessary to calculate the
	  * mean and std deviation of the added data */
	class RunningStats {
		private var cnt: Int = 0
		private var sum: Double = 0
		private var squaredSum: Double = 0

		def add(value: Double): Unit = {
			cnt += 1
			sum += value
			squaredSum += value * value
		}

		/** Adds all the data from another RunningStats
		  *
		  * @param other Object to add
		  */
		def add(other: RunningStats): Unit = {
			cnt += other.cnt
			sum += other.sum
			squaredSum += other.squaredSum
		}

		def count: Int = cnt
		def mean: Double = if (cnt == 0) 0 else sum / cnt
		def stdDev: Double = {
			if (cnt == 0) 0 else {
				val m = mean
				scala.math.sqrt(squaredSum / cnt - m * m)
			}
		}
	}
	object RunningStats {
		def apply(s: Iterable[Double]): RunningStats = {
			val rval = new RunningStats
			s.foreach(rval.add)
			rval
		}
	}
	implicit val encodeRunningStats: Encoder[RunningStats] = Encoder.forProduct3("count", "mean", "SD")(s => (s.count, s.mean, s.stdDev))

	class SeqStats( s: Iterable[Double], min: Option[Double] = None, max: Option[Double] = None, val json_median: Boolean = true, val json_extremes: Boolean = true ) {
		private lazy val filtered = s.filter(v => (min.isEmpty || v >= min.get) && (max.isEmpty || v <= max.get))
		private lazy val f = if (tossed.getOrElse(0) == 0) s else filtered
		private lazy val fsorted = f.sorted
		private lazy val rs = RunningStats(f)
		lazy val tossed: Option[Int] = {
			if (min.isEmpty && max.isEmpty) None
			else {
				val rval = s.size - filtered.size
				if (rval > 0) Some(rval) else None
			}
		}
		lazy val median: Double =	if (fsorted.length == 0) 0.0 else if (fsorted.length == 1) fsorted(0) else {
			val srt = fsorted
			val (lower, upper) = srt.splitAt(srt.length / 2)
			if (s.size % 2 == 0) (lower.last + upper.head) / 2.0 else upper.head
		}
		lazy val MAD: Double = if (fsorted.length == 0 || fsorted.length == 1) 0.0 else {
			val sortedDeviations = s.map(value => (value - median).abs).sorted
			val (lower, upper) = sortedDeviations.splitAt(sortedDeviations.length / 2)
			if (sortedDeviations.size % 2 == 0) (lower.last + upper.head)/2 else upper.head
		}
		lazy val mean: Double = rs.mean
		lazy val stdDev: Double = rs.stdDev
		lazy val count: Int = rs.count
		lazy val min10: Double = RunningStats(fsorted.take(10)).mean
		lazy val max10: Double = RunningStats(fsorted.takeRight(10)).mean
	}
	object SeqStats {
		def apply( s: Iterable[Double], min: Option[Double] = None, max: Option[Double] = None, json_median: Boolean = true ): SeqStats = new SeqStats(s=s, min=min, max=max, json_median=json_median)
	}
	implicit val encodeSeqStats: Encoder[SeqStats] = Encoder.forProduct8(
		"tossed", "count", "median", "MAD", "mean", "SD", "min10", "max10")(
		s => (s.tossed, s.count, if (s.json_median) Some(s.median) else None, if (s.json_median) Some(s.MAD) else None, s.mean, s.stdDev,
			if (s.json_extremes) Some(s.min10) else None,
			if (s.json_extremes) Some(s.max10) else None))

	case class FilteredStats( private val v: Iterable[Double], private val maxMedianFactor: Double = 2.0 ) extends SeqStats(v) {
		lazy val filtered: Option[SeqStats] = {
			val rval = new SeqStats(v, min = Some(median - maxMedianFactor * stdDev), max = Some(median + maxMedianFactor * stdDev), json_median = false)
			if (rval.tossed.getOrElse(0) > 0) Some(rval) else None
		}
	}
	implicit val encodeStatPack: Encoder[FilteredStats] = Encoder.forProduct9(
		"tossed", "count", "median", "MAD", "mean", "SD", "min10", "max10", "filtered")(
		s => (s.tossed, s.count, s.median, s.MAD, s.mean, s.stdDev,
			if (s.json_extremes) Some(s.min10) else None,
			if (s.json_extremes) Some(s.max10) else None,
			s.filtered))

	/** Converts an Option[String] to an int, using default on error */
	def toIntDef(s: String, d: Int): Int = if (s.isEmpty) d else tryElse(s.toInt, d)
	def toIntDef(s: Option[String], d: Int): Int = s.fold(d)(toIntDef(_, d))
	def msToSec(ms: Int): Double = ms / 1000.0
	def dmsToSec(dms: Int): Double = dms / 10000.0

	/** Format Doubles for pretty printing, chop to significant digits and return truncated BigDecimal*/
	import java.math.{MathContext, RoundingMode}
	def prettyDouble(value: Double, significantDigits: Int = 5): BigDecimal = {
		if (significantDigits < 0) throw new IllegalArgumentException
		implicit def bigDecimalToJavaBigDecimal(b: math.BigDecimal): java.math.BigDecimal = b.underlying // Grant us access to Java's BigDecimal.stripTrailingZeros
		val rval = BigDecimal(value, MathContext.DECIMAL64).round(new MathContext(significantDigits, RoundingMode.HALF_UP)).stripTrailingZeros
		if (rval.scale() < 0) rval.setScale(0) else rval
	}

	def sequence[T](seq: Seq[Option[T]]): Option[Seq[T]] = {
		val b = ListBuffer[T]()
		for (x <- seq) {
			if (x.isEmpty) return None
			b += x.get
		}
		Some(b.result)
	}

	def groupConsecutive[T](seq: Seq[T])(equal: (T, T) => Boolean): Seq[Seq[T]] = {
		if (seq.isEmpty) return Seq.empty

		val result = ArrayBuffer(ArrayBuffer(seq.head))

		for (elem <- seq.tail) {
			if (equal(result.last.last, elem)) {
				result.last += elem // Append to the current group
			} else {
				result += ArrayBuffer(elem) // Start a new group
			}
		}

		result.map(_.toSeq) // Convert inner ArrayBuffers to immutable Seqs
	}
}